import {Record, Iterable} from 'immutable';
import {UserIsAnyConditionComponent} from 'components/triggers/conditions';
import UserIsUserConditionComponent from 'components/triggers/conditions/UserIsUserConditionComponent';
import UserInTeamConditionComponent from 'components/triggers/conditions/UserInTeamConditionComponent';
import UserHasManagerConditionComponent from 'components/triggers/conditions/UserHasManagerConditionComponent';
import UserRoleConditionComponent from 'components/triggers/conditions/UserRoleConditionComponent';
import UserIsSharedMeConditionComponent from 'components/triggers/conditions/UserIsSharedMeConditionComponent';
import UserIsSharedConditionComponent from 'components/triggers/conditions/UserIsSharedConditionComponent';
import UserInDepartmentConditionComponent from 'components/triggers/conditions/UserInDepartmentConditionComponent';
import ReviewerIsCallerConditionComponent from 'components/triggers/conditions/ReviewerIsCallerConditionComponent';

const UserConditionType = Object.freeze({
  ANY_USER: 'any_user',
  ROLE: 'role',
  TEAM: 'team',
  DEPARTMENT: 'department',
  USER: 'user',
  MANAGER: 'manager',
  SHARED_ME: 'shared_me',
  SHARED: 'shared',
  REVIEWER_IS_CALLER: 'reviewer_is_caller',
  getUserCondition: (userConditionType, value, label) => {
    switch (userConditionType) {
      case UserConditionType.ANY_USER:
        return new UserIsAnyCondition();
      case UserConditionType.ROLE:
        return new UserRoleCondition({role: value, label});
      case UserConditionType.TEAM:
        return new UserInTeamCondition({team_id: value, label});
      case UserConditionType.DEPARTMENT:
        return new UserInDepartmentCondition({department_id: value, label});
      case UserConditionType.USER:
        return new UserIsUserCondition({user_id: value, label});
      case UserConditionType.MANAGER:
        return new UserHasManagerCondition({manager_id: value, label});
      case UserConditionType.SHARED_ME:
        return new UserIsSharedMeCondition();
      case UserConditionType.SHARED:
        return new UserIsSharedCondition();
      case UserConditionType.REVIEWER_IS_CALLER:
        return new ReviewerIsCallerCondition({negated: value});
      default:
        return new UserIsAnyCondition();
    }
  },
  getUserConditionFromJson: jsonObj => {
    switch (jsonObj.type) {
      case UserConditionType.ANY_USER:
        return new UserIsAnyCondition();
      case UserConditionType.ROLE:
        return new UserRoleCondition(jsonObj);
      case UserConditionType.TEAM:
        return new UserInTeamCondition(jsonObj);
      case UserConditionType.DEPARTMENT:
        return new UserInDepartmentCondition(jsonObj);
      case UserConditionType.USER:
        return new UserIsUserCondition(jsonObj);
      case UserConditionType.MANAGER:
        return new UserHasManagerCondition(jsonObj);
      case UserConditionType.SHARED_ME:
        return new UserIsSharedMeCondition();
      case UserConditionType.SHARED:
        return new UserIsSharedCondition();
      case UserConditionType.REVIEWER_IS_CALLER:
        return new ReviewerIsCallerCondition(jsonObj);
      default:
        return new UserIsAnyCondition();
    }
  },

  ensureUserCondition(obj) {
    if (!obj) {
      return obj;
    }
    if (Iterable.isIterable(obj)) {
      return obj;
    }
    return UserConditionType.getUserConditionFromJson(obj);
  },

  isUserCondition(type) {
    return [
      UserConditionType.ANY_USER,
      UserConditionType.USER,
      UserConditionType.MANAGER,
      UserConditionType.TEAM,
      UserConditionType.DEPARTMENT,
      UserConditionType.ROLE,
      UserConditionType.SHARED_ME,
      UserConditionType.SHARED,
      UserConditionType.REVIEWER_IS_CALLER,
    ].includes(type);
  },
});

class UserIsAnyCondition extends Record({
  type: UserConditionType.ANY_USER,
  component: UserIsAnyConditionComponent,
}) {
  getValue() {
    return null;
  }
}

class UserIsUserCondition extends Record({
  type: UserConditionType.USER,
  component: UserIsUserConditionComponent,
  user_id: null,
  label: null,
}) {
  constructor(props = {}) {
    super({
      ...props,
      user_id: props.user_id || props.userId,
    });
  }

  getValue() {
    return this.user_id;
  }

  ready() {
    return !!this.user_id;
  }
}

class UserRoleCondition extends Record({
  type: UserConditionType.ROLE,
  role: null,
  label: null,
  component: UserRoleConditionComponent,
}) {
  getValue() {
    return this.role;
  }
}

class UserInTeamCondition extends Record({
  type: UserConditionType.TEAM,
  team_id: null,
  label: null,
  component: UserInTeamConditionComponent,
}) {
  constructor(props = {}) {
    super({
      ...props,
      team_id: props.team_id || props.teamId,
    });
  }

  getValue() {
    return this.team_id;
  }
}

class UserInDepartmentCondition extends Record({
  type: UserConditionType.DEPARTMENT,
  department_id: null,
  label: null,
  component: UserInDepartmentConditionComponent,
}) {
  constructor(props = {}) {
    super({
      ...props,
      department_id: props.department_id || props.departmentId,
    });
  }

  getValue() {
    return this.department_id;
  }
}

class UserHasManagerCondition extends Record({
  type: UserConditionType.MANAGER,
  manager_id: null,
  label: null,
  component: UserHasManagerConditionComponent,
}) {
  constructor(props = {}) {
    super({
      ...props,
      manager_id: props.manager_id || props.managerId,
    });
  }

  getValue() {
    return this.manager_id;
  }
}

class UserIsSharedMeCondition extends Record({
  type: UserConditionType.SHARED_ME,
  component: UserIsSharedMeConditionComponent,
}) {
  getValue() {
    return null;
  }
}

class UserIsSharedCondition extends Record({
  type: UserConditionType.SHARED,
  component: UserIsSharedConditionComponent,
}) {
  getValue() {
    return null;
  }
}

class ReviewerIsCallerCondition extends Record({
  type: UserConditionType.REVIEWER_IS_CALLER,
  negated: false,
  component: ReviewerIsCallerConditionComponent,
}) {
  getValue() {
    return this.negated;
  }
}

export {
  UserConditionType,
  UserIsAnyCondition,
  UserRoleCondition,
  UserInTeamCondition,
  UserInDepartmentCondition,
  UserIsUserCondition,
  UserHasManagerCondition,
  UserIsSharedMeCondition,
  ReviewerIsCallerCondition,
};
